import { ImageField, LinkField, StringField } from '../../../types/types';
import { appendRootToInternalLink } from '../../../utils/appendRootToInternalLink.utils';
import { appendRootToRichTextInternalLinks } from '../../../utils/appendRootToRichTextInternalLinks.utils';
import {
  ProductTestimonialProps,
  ProductTestimonialRendererFields,
  ProductTestimonialSlideFields,
  ProductTestimonialRendererParams,
  TAsset,
} from '../types/productSeriesDetailTestimonial.types';

export class ProductTestimonialSlideModel {
  private readonly card: ProductTestimonialSlideFields;
  private readonly itemIndex: number;
  constructor(Card, index) {
    this.card = Card || null;
    this.itemIndex = index;
  }

  get id(): string {
    return this.card?.id ?? '';
  }

  get title(): StringField {
    return { value: this.card?.title };
  }

  get discount(): StringField {
    return { value: this.card?.discount };
  }

  get description(): StringField {
    return { value: appendRootToRichTextInternalLinks(this.card?.description) };
  }

  get type(): StringField {
    const defaultValue = { value: 'offerCardRegular' };
    if (this.itemIndex === 0) defaultValue.value = 'offerCardWide';
    return defaultValue;
  }

  get disclaimer(): string {
    return this.card?.disclaimer;
  }

  get expiration(): StringField {
    return { value: this.card?.expiration };
  }

  get badge(): string {
    return this.card?.badge;
  }

  get expirationLabel(): StringField {
    return { value: this.card?.expirationLabel };
  }

  get image(): ImageField {
    return {
      value: {
        src: this.card?.assets?.[0]?.rendition_desktop_size1000?.url,
        alt: this.card?.assets?.[0]?.alt,
      },
    };
  }

  get mobileImage(): ImageField {
    return {
      value: {
        src: this.card?.assets?.[0]?.rendition_mobile_size500?.url,
        alt: this.card?.assets?.[0]?.alt,
      },
    };
  }

  get asset(): TAsset | undefined {
    return this.card?.assets?.[0];
  }

  get imageAlt(): string | undefined {
    return this.card?.assets && this.card?.assets?.[0]?.alt;
  }

  get buttonPrimary(): LinkField {
    return {
      value: {
        href: appendRootToInternalLink(this.card?.button1?.url),
        title: this.card?.button1?.label,
        target: this.card?.button1?.target,
      },
    };
  }

  get buttonSecondary(): LinkField {
    return {
      value: {
        href: appendRootToInternalLink(this.card?.button2?.url),
        title: this.card?.button2?.label,
        target: this.card?.button2?.target,
      },
    };
  }

  get cardStyles(): StringField {
    return { value: this.card?.cardStyles };
  }
}

export class ProductTestimonialModel {
  private readonly fields: null | ProductTestimonialRendererFields;
  private readonly params: null | ProductTestimonialRendererParams;
  constructor(props: ProductTestimonialProps) {
    this.fields = props?.fields || null;
    this.params = props?.params || null;
  }

  get eyebrow(): StringField {
    return { value: this.fields?.apiData?.eyebrow };
  }

  get title(): StringField {
    return { value: this.fields?.apiData?.title };
  }

  get description(): StringField {
    return { value: appendRootToRichTextInternalLinks(this.fields?.apiData?.description) };
  }

  get sectionCtaSecondary(): LinkField {
    return {
      value: {
        href: appendRootToInternalLink(this.fields?.apiData?.button2?.url),
        title: this.fields?.apiData?.button2?.label,
        target: this.fields?.apiData?.button2?.target,
      },
    };
  }
  get innerComponents() {
    if (this.fields?.apiData?.innerComponents) {
      const cardStyles = (this.params?.cardStyles && this.params?.cardStyles.split('|')) || [];
      return this.fields?.apiData?.innerComponents?.map((Card, index) => {
        Card['cardStyles'] = cardStyles[index];
        return new ProductTestimonialSlideModel(Card, index);
      });
    }
    return [];
  }
}
